import { ReadSyncableStateStatisticsDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import { usePolling } from 'modules/integrations/hooks/usePolling'

const SYNCABLE_STATE_STATISTICS_POLLING_INTERVAL_MS = 3 * 60 * 1000

export const useSyncableStateStatistics = ({ skip = false }: { skip?: boolean } = {}) => {
  const {
    syncEnabled,
    marketplaceIntegration,
    configuration,
    dataCollectionGraphqlJsonString,
    companyId,
    contextParameters,
    isBookkeeperView,
  } = usePrivateIntegrationsSyncContext()

  const pollingEnabled = configuration.pollingEnabled ?? false

  const [fetchSyncableStateStatistics, { data, loading }] = useLazyQuery(
    ReadSyncableStateStatisticsDocument
  )

  const serializedContextParameters = JSON.stringify(contextParameters)

  const canFetch = !skip && !!syncEnabled && !!marketplaceIntegration && !!companyId

  useUnsafeEffect(() => {
    if (!canFetch) return

    fetchSyncableStateStatistics({
      variables: {
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
        graphqlVariablesJson: dataCollectionGraphqlJsonString,
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [
    canFetch,
    marketplaceIntegration,
    companyId,
    isBookkeeperView,
    configuration.origin,
    serializedContextParameters,
    dataCollectionGraphqlJsonString,
    fetchSyncableStateStatistics,
  ])

  // Poll statistics while the sync run is not actively running.
  // This ensures the statistics update after a sync run completes without needing subscriptions.
  usePolling({
    enabled: pollingEnabled && canFetch,
    callback: () => {
      if (!companyId || !marketplaceIntegration) return

      fetchSyncableStateStatistics({
        variables: {
          companyId,
          marketplaceIntegrationId: marketplaceIntegration.id,
          origin: configuration.origin,
          parameters: toSyncParameters(contextParameters),
          graphqlVariablesJson: dataCollectionGraphqlJsonString,
        },
        context: {
          isBookkeeperView,
        },
        fetchPolicy: 'network-only',
      })
    },
    intervalMs: SYNCABLE_STATE_STATISTICS_POLLING_INTERVAL_MS,
  })

  const statistics = data?.integrations?.syncableStateStatisticsConnection?.nodes?.[0]

  // null  → statistics won't be loaded (skipped or no integration available)
  // undefined → statistics are being fetched
  // object → statistics ready
  const resolvedStatistics = canFetch ? statistics : null

  return { statistics: resolvedStatistics, loading }
}
